import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Zap, Play, Trash2, Power } from "lucide-react";
import { toast } from "sonner";

const TRIGGERS = ["manual", "stakeholder.stage_changed", "proposal.signed", "task.completed", "meeting.summarized"];
const ACTIONS = ["notify", "create_task", "log_communication", "send_email_draft"];

const ACTION_COLOR = {
  notify: "#3B82F6",
  create_task: "#22C55E",
  log_communication: "#A855F7",
  send_email_draft: "#D4AF37",
};

export default function Automation() {
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ trigger: "manual", action: "notify", enabled: true });
  const [projects, setProjects] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);

  const refresh = async () => {
    const [r1, r2, r3, r4] = await Promise.all([
      api.get("/automation/rules"),
      api.get("/automation/runs"),
      api.get("/projects"),
      api.get("/stakeholders"),
    ]);
    setRules(r1.data);
    setRuns(r2.data);
    setProjects(r3.data);
    setStakeholders(r4.data);
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    const payload = {
      name: form.name,
      trigger: form.trigger,
      action: form.action,
      enabled: form.enabled,
      trigger_filter: {},
      action_config: buildActionConfig(form),
    };
    try {
      await api.post("/automation/rules", payload);
      setOpen(false); setForm({ trigger: "manual", action: "notify", enabled: true });
      refresh(); toast.success("Rule saved");
    } catch (e) { toast.error("Failed"); }
  };

  const buildActionConfig = (f) => {
    switch (f.action) {
      case "notify": return { title: f.notify_title || "Automation notice", body: f.notify_body || "Rule executed." };
      case "create_task": return { project_id: f.project_id, title: f.task_title, priority: f.task_priority || "medium" };
      case "log_communication": return { stakeholder_id: f.stakeholder_id, subject: f.comm_subject, content: f.comm_content };
      case "send_email_draft": return { to: f.email_to, subject: f.email_subject, body: f.email_body };
      default: return {};
    }
  };

  const run = async (id) => {
    try {
      await api.post(`/automation/rules/${id}/run`);
      toast.success("Rule executed");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (r) => {
    await api.put(`/automation/rules/${r.id}`, { enabled: !r.enabled });
    refresh();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete rule?")) return;
    await api.delete(`/automation/rules/${id}`);
    refresh();
  };

  return (
    <div className="p-8 space-y-6" data-testid="automation-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Automation Hub</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Workflow Automation</h1>
          <p className="text-slate-400 mt-2 text-sm">Trigger-based actions across the operating system.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="new-rule-btn"
          className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          <Plus className="w-4 h-4" /> New Rule
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-3">
          <div className="label-eyebrow">// Rules ({rules.length})</div>
          {rules.map((r) => (
            <motion.div layout key={r.id} className="nexus-card p-5" data-testid={`rule-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 ${r.enabled ? "" : "opacity-30"}`} style={{ background: ACTION_COLOR[r.action] }}></span>
                    <span className="label-eyebrow">{r.enabled ? "active" : "disabled"}</span>
                  </div>
                  <div className="font-display text-lg text-white">{r.name}</div>
                  <div className="mt-3 flex items-center gap-3 text-xs font-mono">
                    <span className="text-slate-400">WHEN</span>
                    <span className="text-white px-2 py-0.5 border border-white/10">{r.trigger}</span>
                    <span className="text-slate-400">→</span>
                    <span className="text-slate-400">DO</span>
                    <span className="px-2 py-0.5 border" style={{ borderColor: ACTION_COLOR[r.action], color: ACTION_COLOR[r.action] }}>{r.action}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono mt-3">
                    Executed {r.run_count || 0}× · last {r.last_run ? new Date(r.last_run).toLocaleString("en-US", { hour12: false }) : "never"}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => run(r.id)} data-testid={`run-${r.id}`} className="p-2 border border-white/10 hover:border-[#D4AF37]/50 text-[#D4AF37]" title="Run now">
                    <Play className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                  <button onClick={() => toggle(r)} className="p-2 border border-white/10 hover:border-white/30 text-slate-400" title={r.enabled ? "Disable" : "Enable"}>
                    <Power className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(r.id)} className="p-2 border border-white/10 hover:border-red-500/50 text-slate-500 hover:text-red-400">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
          {!rules.length && <div className="nexus-card p-8 text-center text-sm text-slate-500">No rules yet. Create your first.</div>}
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="label-eyebrow mb-2">// Execution Log ({runs.length})</div>
          <div className="nexus-card max-h-[70vh] overflow-y-auto">
            {runs.map((r) => (
              <div key={r.id} className="px-4 py-3 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-2">
                  <Zap className="w-3 h-3" style={{ color: "var(--gold-400)" }} />
                  <span className="text-xs text-white">{r.outcome}</span>
                </div>
                <div className="font-mono text-[10px] text-slate-500 mt-1">{new Date(r.timestamp).toLocaleString("en-US", { hour12: false })}</div>
              </div>
            ))}
            {!runs.length && <div className="p-6 text-center text-xs font-mono text-slate-500">No executions yet</div>}
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">// New Rule</div>
                <h2 className="font-display text-2xl text-white">Define Workflow</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Rule name *">
                <input data-testid="rule-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="au-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="When (trigger)">
                  <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} className="au-input">
                    {TRIGGERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Do (action)">
                  <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="au-input">
                    {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </Field>
              </div>

              {form.action === "notify" && (
                <>
                  <Field label="Notification title">
                    <input value={form.notify_title || ""} onChange={(e) => setForm({ ...form, notify_title: e.target.value })} className="au-input" />
                  </Field>
                  <Field label="Notification body">
                    <textarea rows={2} value={form.notify_body || ""} onChange={(e) => setForm({ ...form, notify_body: e.target.value })} className="au-input resize-none" />
                  </Field>
                </>
              )}
              {form.action === "create_task" && (
                <>
                  <Field label="Target project">
                    <select value={form.project_id || ""} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="au-input">
                      <option value="">— select project —</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Task title">
                    <input value={form.task_title || ""} onChange={(e) => setForm({ ...form, task_title: e.target.value })} className="au-input" />
                  </Field>
                </>
              )}
              {form.action === "log_communication" && (
                <>
                  <Field label="Stakeholder">
                    <select value={form.stakeholder_id || ""} onChange={(e) => setForm({ ...form, stakeholder_id: e.target.value })} className="au-input">
                      <option value="">— select stakeholder —</option>
                      {stakeholders.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.organization}</option>)}
                    </select>
                  </Field>
                  <Field label="Subject">
                    <input value={form.comm_subject || ""} onChange={(e) => setForm({ ...form, comm_subject: e.target.value })} className="au-input" />
                  </Field>
                  <Field label="Content">
                    <textarea rows={3} value={form.comm_content || ""} onChange={(e) => setForm({ ...form, comm_content: e.target.value })} className="au-input resize-none" />
                  </Field>
                </>
              )}
              {form.action === "send_email_draft" && (
                <>
                  <Field label="Recipient">
                    <input value={form.email_to || ""} onChange={(e) => setForm({ ...form, email_to: e.target.value })} className="au-input" />
                  </Field>
                  <Field label="Subject">
                    <input value={form.email_subject || ""} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} className="au-input" />
                  </Field>
                  <Field label="Body">
                    <textarea rows={4} value={form.email_body || ""} onChange={(e) => setForm({ ...form, email_body: e.target.value })} className="au-input resize-none" />
                  </Field>
                </>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button onClick={save} data-testid="save-rule-btn" className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Save Rule</button>
            </div>
          </motion.div>
        </div>
      )}
      <style>{`.au-input { width:100%; padding:0.5rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .au-input:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}
const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);
